import { Dialog } from 'primereact/dialog';
import MultipleSelectStyled from '../../../components/inputs/StyledInputs/MultipleSelectStyled';
import { useMemo, useState } from 'react';
import {
  AccessType,
  ApprovalSequenceFormFields,
  Member,
} from '../../../Interfaces/admin/ApprovalSequence.interface';
import { useQuery } from '@tanstack/react-query';
import { getAccess } from '../../../services/admin/ApprovalSequences';
import { Button } from 'primereact/button';
import { useWatch } from 'react-hook-form';

export type AccessSelectorDropdownProps = {
  show: boolean;
  hide: () => void;
  selectedField: { members: Member[]; step: number };
  selectedStepIndex: number;
  updateStep: (
    index: number,
    fieldDetails: { step: number; members: Member[] }
  ) => void;
};

const AccessSelectorDropdown = ({
  show,
  hide,
  selectedField,
  selectedStepIndex,
  updateStep,
}: AccessSelectorDropdownProps) => {
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [memberType, setMemberType] = useState<AccessType[]>([]);
  const [company, report] = useWatch<ApprovalSequenceFormFields>({
    name: ['company', 'report'],
  });
  const isFetchAccessEnabled = !!(company && report && memberType.length);

  const getAccessInfo = useQuery({
    queryKey: ['getAccess', company, report, memberType],
    queryFn: ({ signal }) =>
      getAccess(
        {
          companyID: company as string,
          report: report as string,
          type: [AccessType.role, AccessType.user],
          enabled: true,
          shouldApprove: true,
        },
        signal
      ),
    refetchOnWindowFocus: false,
    enabled: isFetchAccessEnabled,
  });

  const selectMembersReset = () => {
    setMemberType([]);
    setSelectedMembers([]);
  };

  const accessList = useMemo(() => {
    if (!getAccessInfo.data) {
      return [];
    }

    getAccessInfo.data.sort((a, b) => {
      if (a.user && b.user) {
        return a.user.name.localeCompare(b.user.name);
      }

      if (a.user && !b.user) {
        return -1;
      }

      if (!a.user && b.user) {
        return 1;
      }

      return 0;
    });

    const includeRoles = memberType.includes(AccessType.role);
    const includeUser = memberType.includes(AccessType.user);
    const members = selectedField.members;
    const result = getAccessInfo.data.reduce((acc, access) => {
      const accessInfo: typeof acc[0] = {
        id: access.id,
        name: access.role ? access.role.name : access.user?.name ?? '',
        disabled: !!members.find((member) => member.id === access.id),
      };

      if ((includeRoles && access.role) || (includeUser && access.user)) {
        acc.push(accessInfo);
      }

      if (includeUser && access.role?.users) {
        access.role?.users.forEach((user) => {
          if (!acc.find((access) => access.assigneeId === user.id)) {
            const assigneeAccessId = `${access.id}--${user.id}`;
            acc.push({
              id: assigneeAccessId,
              name: user.name,
              assigneeId: user.id,
              disabled: !!members.find(
                (member) =>
                  member.id === assigneeAccessId || member.id === access.id
              ),
            });
          }
        });
      }

      return acc;
    }, [] as { id: string; name: string; assigneeId?: string; disabled?: boolean }[]);

    return result;
  }, [getAccessInfo.data, memberType, selectedField]);

  return (
    <Dialog
      header="New Sequence Member"
      visible={show}
      onHide={() => {
        hide();
        selectMembersReset();
      }}
      className="max-w-500px"
      contentClassName="flex justify-content-center"
    >
      <div className="grid justify-content-center w-full">
        <div className="col-12">
          <label htmlFor="type" className="text-standard block mb-2">
            Type
          </label>
          <MultipleSelectStyled
            id="type"
            options={['user', 'role']}
            placeholder="Select member type"
            clearable={false}
            value={memberType}
            onChange={(e) => {
              setMemberType(e.value);
              setSelectedMembers([]);
            }}
          />
        </div>
        <div className="col-12">
          <label htmlFor="members" className="text-standard block mb-2">
            Members
          </label>
          <MultipleSelectStyled
            id="members"
            isLoading={getAccessInfo.isLoading}
            isDisabled={getAccessInfo.isError || !isFetchAccessEnabled}
            tooltip={
              !isFetchAccessEnabled
                ? 'To enable the members selector please fill out: Company, Report and Type.'
                : undefined
            }
            tooltipOptions={{ showOnDisabled: true, position: 'top' }}
            display="chip"
            options={accessList}
            labelField="name"
            valueField="id"
            placeholder={
              getAccessInfo.isError
                ? 'Failed to load members!'
                : 'Select members'
            }
            clearable={false}
            filter={true}
            resetFilterOnHide={true}
            value={selectedMembers}
            onChange={(e) => {
              if (e.value.length > selectedMembers.length) {
                let membersList: string[] = e.value;
                const latestElement = e.value[e.value.length - 1] as string;
                if (!latestElement.includes('--')) {
                  accessList.forEach((access) => {
                    if (access.id.includes(`${latestElement}--`)) {
                      access.disabled = true;
                      membersList = membersList.filter(
                        (member) => member !== access.id
                      );
                    }
                  });
                }
                setSelectedMembers(membersList);
              } else {
                const removedMember = selectedMembers.find(
                  (memberId) => !(e.value as string[]).includes(memberId)
                );

                if (removedMember && !removedMember.includes('--')) {
                  accessList.forEach((access) => {
                    if (access.id.includes(`${removedMember}--`)) {
                      const members = selectedField.members;
                      access.disabled = !!members.find(
                        (member) => member.id === access.id
                      );
                    }
                  });
                }
                setSelectedMembers(e.value);
              }
            }}
            showSelectAll={false}
          />
        </div>
        <Button
          onClick={() => {
            const newMembers = accessList.filter((element) =>
              selectedMembers.includes(element.id)
            );
            if (newMembers?.length) {
              let baseMembers = selectedField.members;
              newMembers.forEach((newMember) => {
                if (!newMember.id.includes('--')) {
                  baseMembers = baseMembers.filter(
                    (member) => !member.id.includes(`${newMember.id}--`)
                  );
                }
              });

              updateStep(selectedStepIndex, {
                step: selectedField.step,
                members: [...baseMembers, ...newMembers],
              });
            }
            hide();
            selectMembersReset();
          }}
          className="w-15rem bg-green-500 border-0 flex m-auto py-3"
          disabled={!selectedMembers.length}
        >
          <span className="mx-auto text-22px text-white">Add</span>
        </Button>
      </div>
    </Dialog>
  );
};

export default AccessSelectorDropdown;
